<script setup lang="ts">
import { computed, onBeforeUnmount, onMounted, ref, watch } from 'vue';
import dayjs, { type Dayjs } from 'dayjs';
import DayCell from './DayCell.vue';
import { stores } from '@/util/stores.js';
import { isAvailabilityBlockType, isBlockingBlockType } from '@/types/availability';
import { userFirstDayOfWeek } from '@/composables/useUserPreferences';
import './availability.css';

interface CalendarDay {
	date: string;
	dayjs: Dayjs;
	isCurrentMonth: boolean;
	isToday: boolean;
	isWeekend: boolean;
}

interface MatchRecord extends Record<string, unknown> {
	matchDate?: string | null;
	matchTime?: string | null;
}

const props = withDefaults(
	defineProps<{
		initialDate?: Date | string;
		selectedDates?: string[];
		mobileMode?: boolean;
	}>(),
	{
		initialDate: () => new Date(),
		selectedDates: () => [],
		mobileMode: false
	}
);

const emit = defineEmits(['dateClick', 'dateRangeSelect', 'monthChange', 'setAvailabilityFromHover', 'copyPreviousMonth']);
const selectedDates = computed(() => props.selectedDates ?? []);
const mobileMode = computed(() => props.mobileMode ?? false);

// State
const currentDate = ref(dayjs(props.initialDate));
const view = ref<'month' | 'week'>('month');
const weekStartsOn = computed(() => (userFirstDayOfWeek() === 'MONDAY' ? 1 : 0));
const isDragging = ref(false);
const dragStart = ref<string | null>(null);
const dragEnd = ref<string | null>(null);
const rafId = ref<number | null>(null);

// Jump to month picker state
const showJumpPicker = ref(false);
const jumpMonth = ref(dayjs().month());
const jumpYear = ref(dayjs().year());

// Month names for picker
const monthNames = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];

// Year range for picker (5 years back, 5 years forward)
const yearOptions = computed(() => {
	const current = dayjs().year();
	const years: number[] = [];
	for (let y = current - 2; y <= current + 5; y++) {
		years.push(y);
	}
	return years;
});

/**
 * Get the smart default month to display.
 * If 7 or fewer days remain in the current month, default to next month.
 */
function getSmartDefaultDate() {
	const now = dayjs();
	const endOfMonth = now.endOf('month');
	const daysRemaining = endOfMonth.diff(now, 'day');

	if (daysRemaining <= 7) {
		return now.add(1, 'month').startOf('month');
	}

	return now;
}

// Computed
const monthName = computed(() => currentDate.value.format('MMMM YYYY'));
function startOfPreferredWeek(date: Dayjs): Dayjs {
	return date.subtract((date.day() - weekStartsOn.value + 7) % 7, 'day').startOf('day');
}

function isWeekend(date: Dayjs): boolean {
	return date.day() === 0 || date.day() === 6;
}

const weekName = computed(() => {
	const start = startOfPreferredWeek(currentDate.value);
	const end = start.add(6, 'day');
	if (start.month() === end.month()) {
		return `${start.format('MMMM D')} - ${end.format('D, YYYY')}`;
	}
	return `${start.format('MMM D')} - ${end.format('MMM D, YYYY')}`;
});
const headerTitle = computed(() => (view.value === 'week' ? weekName.value : monthName.value));
const today = computed(() => dayjs().format('YYYY-MM-DD'));

const visibleMonthMatchSuggestions = computed(() => {
	return stores.availabilityStore.suggestions.filter((suggestion) => suggestion.matchDay && dayjs(suggestion.date).isSame(currentDate.value, 'month'));
});

const visibleMonthMatchCount = computed(() => {
	return visibleMonthMatchSuggestions.value.reduce((total, suggestion) => {
		const summaryCount = Number(suggestion.matchCount);
		if (Number.isFinite(summaryCount) && summaryCount > 0) {
			return total + summaryCount;
		}
		return total + (Array.isArray(suggestion.matches) ? suggestion.matches.length : 0);
	}, 0);
});

const visibleMonthMatchDateCount = computed(() => {
	return new Set(visibleMonthMatchSuggestions.value.map((suggestion) => dayjs(suggestion.date).format('YYYY-MM-DD'))).size;
});

const visibleMonthAttentionCount = computed(() => {
	return new Set(stores.availabilityStore.datesNeedingAttention.filter((date) => dayjs(date).isSame(currentDate.value, 'month')).map((date) => dayjs(date).format('YYYY-MM-DD'))).size;
});

const visibleMonthMatchSummary = computed(() => {
	const matchCount = visibleMonthMatchCount.value;
	const dateCount = visibleMonthMatchDateCount.value;
	if (matchCount === 0) return 'No matches this month';

	const matchLabel = `${matchCount} match${matchCount === 1 ? '' : 'es'}`;
	return dateCount > 1 ? `${matchLabel} across ${dateCount} dates` : matchLabel;
});

const visibleMonthAvailabilitySummary = computed(() => {
	if (visibleMonthMatchCount.value === 0) return '';
	const count = visibleMonthAttentionCount.value;
	return count > 0 ? `${count} date${count === 1 ? '' : 's'} need${count === 1 ? 's' : ''} availability` : 'Availability covered';
});

const todayInDifferentMonth = computed(() => {
	const now = dayjs();
	return now.month() !== currentDate.value.month() || now.year() !== currentDate.value.year();
});

const todayFormatted = computed(() => dayjs().format('MMM D'));

const weekDays = computed(() => {
	const start = startOfPreferredWeek(currentDate.value);
	const days: CalendarDay[] = [];
	for (let i = 0; i < 7; i++) {
		const day = start.add(i, 'day');
		days.push({
			date: day.format('YYYY-MM-DD'),
			dayjs: day,
			isCurrentMonth: day.month() === currentDate.value.month(),
			isToday: day.format('YYYY-MM-DD') === today.value,
			isWeekend: isWeekend(day)
		});
	}
	return days;
});

const weeks = computed(() => {
	const startOfMonth = currentDate.value.startOf('month');
	const endOfMonth = currentDate.value.endOf('month');

	let start = startOfPreferredWeek(startOfMonth);
	const end = startOfPreferredWeek(endOfMonth).add(6, 'day');

	const weeksArray: CalendarDay[][] = [];
	let currentWeek: CalendarDay[] = [];

	while (start.isBefore(end) || start.isSame(end, 'day')) {
		currentWeek.push({
			date: start.format('YYYY-MM-DD'),
			dayjs: start,
			isCurrentMonth: start.month() === currentDate.value.month(),
			isToday: start.format('YYYY-MM-DD') === today.value,
			isWeekend: isWeekend(start)
		});

		if (currentWeek.length === 7) {
			weeksArray.push(currentWeek);
			currentWeek = [];
		}

		start = start.add(1, 'day');
	}

	return weeksArray;
});

const weekDayHeaders = computed(() => {
	const labels = [
		{ key: 'SUN', full: 'SUN', mobile: 'S', isWeekend: true },
		{ key: 'MON', full: 'MON', mobile: 'M', isWeekend: false },
		{ key: 'TUE', full: 'TUE', mobile: 'T', isWeekend: false },
		{ key: 'WED', full: 'WED', mobile: 'W', isWeekend: false },
		{ key: 'THU', full: 'THU', mobile: 'T', isWeekend: false },
		{ key: 'FRI', full: 'FRI', mobile: 'F', isWeekend: false },
		{ key: 'SAT', full: 'SAT', mobile: 'S', isWeekend: true }
	];
	return [...labels.slice(weekStartsOn.value), ...labels.slice(0, weekStartsOn.value)].map((day) => ({ ...day, label: mobileMode.value ? day.mobile : day.full }));
});

const previousMonthName = computed(() => {
	return currentDate.value.subtract(1, 'month').format('MMMM');
});

// Drag selection state
const dragMode = ref<'column' | 'free' | null>(null);
const dragStartColumn = ref<number | null>(null);
const dragEndColumn = ref<number | null>(null);
const dragStartRow = ref<number | null>(null);
const dragEndRow = ref<number | null>(null);

const selectedDateRange = computed(() => {
	if (!dragStart.value) return new Set(selectedDates.value);

	const dates = new Set(selectedDates.value);

	if (dragMode.value === 'column' && dragStartColumn.value !== null && dragStartRow.value !== null) {
		const startCol = dragStartColumn.value;
		const endCol = dragEndColumn.value ?? startCol;
		const startRow = dragStartRow.value;
		const endRow = dragEndRow.value ?? startRow;

		const minCol = Math.min(startCol, endCol);
		const maxCol = Math.max(startCol, endCol);
		const minRow = Math.min(startRow, endRow);
		const maxRow = Math.max(startRow, endRow);

		for (let rowIdx = minRow; rowIdx <= maxRow; rowIdx++) {
			const week = weeks.value[rowIdx];
			if (!week) continue;

			for (let col = minCol; col <= maxCol; col++) {
				const day = week[col];
				if (day) {
					dates.add(day.date);
				}
			}
		}
	} else {
		const start = dayjs(dragStart.value);
		const end = dragEnd.value ? dayjs(dragEnd.value) : start;

		let current = start.isBefore(end) ? start : end;
		const last = start.isBefore(end) ? end : start;

		while (current.isBefore(last) || current.isSame(last, 'day')) {
			dates.add(current.format('YYYY-MM-DD'));
			current = current.add(1, 'day');
		}
	}

	return dates;
});

// Methods
function prev() {
	if (view.value === 'week') {
		currentDate.value = currentDate.value.subtract(1, 'week');
	} else {
		currentDate.value = currentDate.value.subtract(1, 'month');
	}
	emit('monthChange', currentDate.value.toDate());
}

function next() {
	if (view.value === 'week') {
		currentDate.value = currentDate.value.add(1, 'week');
	} else {
		currentDate.value = currentDate.value.add(1, 'month');
	}
	emit('monthChange', currentDate.value.toDate());
}

function goToToday() {
	currentDate.value = dayjs();
	emit('monthChange', currentDate.value.toDate());
}

function copyPreviousMonth() {
	const prevMonth = currentDate.value.subtract(1, 'month');
	const targetMonth = currentDate.value;
	emit('copyPreviousMonth', {
		sourceMonth: prevMonth.format('YYYY-MM'),
		targetMonth: targetMonth.format('YYYY-MM')
	});
}

function setView(newView: 'month' | 'week') {
	view.value = newView;
}

function openJumpPicker() {
	jumpMonth.value = currentDate.value.month();
	jumpYear.value = currentDate.value.year();
	showJumpPicker.value = true;
}

function closeJumpPicker() {
	showJumpPicker.value = false;
}

function confirmJump() {
	currentDate.value = dayjs().year(jumpYear.value).month(jumpMonth.value).date(1);
	emit('monthChange', currentDate.value.toDate());
	showJumpPicker.value = false;
}

// Weekend quick selection methods
function getDatesForWeekday(dayOfWeek: number) {
	const dates: string[] = [];
	const todayDate = dayjs().startOf('day');

	for (const week of weeks.value) {
		for (const day of week) {
			const d = dayjs(day.date);
			if (day.isCurrentMonth && d.day() === dayOfWeek && !d.isBefore(todayDate)) {
				dates.push(day.date);
			}
		}
	}
	return dates;
}

function getAllSaturdays() {
	return getDatesForWeekday(6);
}

function getAllSundays() {
	return getDatesForWeekday(0);
}

function getAllWeekends() {
	return [...getAllSaturdays(), ...getAllSundays()].sort();
}

function selectAllSaturdays() {
	const dates = getAllSaturdays();
	if (dates.length > 0) {
		emit('dateRangeSelect', {
			dates,
			start: dates[0],
			end: dates[dates.length - 1],
			quickAction: 'saturdays'
		});
	}
}

function selectAllSundays() {
	const dates = getAllSundays();
	if (dates.length > 0) {
		emit('dateRangeSelect', {
			dates,
			start: dates[0],
			end: dates[dates.length - 1],
			quickAction: 'sundays'
		});
	}
}

function selectAllWeekends() {
	const dates = getAllWeekends();
	if (dates.length > 0) {
		emit('dateRangeSelect', {
			dates,
			start: dates[0],
			end: dates[dates.length - 1],
			quickAction: 'weekends'
		});
	}
}

function getColumnForDate(dateStr: string) {
	const d = dayjs(dateStr);
	return (d.day() - weekStartsOn.value + 7) % 7;
}

function getRowForDate(dateStr: string) {
	for (let i = 0; i < weeks.value.length; i++) {
		const week = weeks.value[i];
		if (week?.some((d) => d.date === dateStr)) {
			return i;
		}
	}
	return -1;
}

function getDatesInColumnsAndRows(columns: number[], startRow: number, endRow: number, filterPast = false) {
	const dates: string[] = [];
	const todayDate = dayjs().startOf('day');

	const minRow = Math.min(startRow, endRow);
	const maxRow = Math.max(startRow, endRow);

	for (let rowIdx = minRow; rowIdx <= maxRow; rowIdx++) {
		const week = weeks.value[rowIdx];
		if (!week) continue;

		for (const col of columns) {
			const day = week[col];
			if (day) {
				if (day.isCurrentMonth) {
					if (filterPast) {
						const d = dayjs(day.date);
						if (!d.isBefore(todayDate)) {
							dates.push(day.date);
						}
					} else {
						dates.push(day.date);
					}
				}
			}
		}
	}
	return dates.sort();
}

function getColumnRange(startCol: number, endCol: number) {
	const min = Math.min(startCol, endCol);
	const max = Math.max(startCol, endCol);
	const cols: number[] = [];
	for (let c = min; c <= max; c++) {
		cols.push(c);
	}
	return cols;
}

// Drag selection handlers
function handleDragStart({ date }: { date: string }) {
	isDragging.value = true;
	dragStart.value = date;
	dragEnd.value = date;
	dragMode.value = null;
	dragStartColumn.value = getColumnForDate(date);
	dragEndColumn.value = dragStartColumn.value;
	dragStartRow.value = getRowForDate(date);
	dragEndRow.value = dragStartRow.value;
}

function handleDragEnter({ date }: { date: string }) {
	if (!isDragging.value) return;

	const currentColumn = getColumnForDate(date);
	const currentRow = getRowForDate(date);
	const startColumn = dragStartColumn.value;
	const startRow = dragStartRow.value;
	if (startColumn === null || startRow === null) return;

	if (dragMode.value === null && date !== dragStart.value) {
		const rowDiff = Math.abs(currentRow - startRow);
		const colDiff = Math.abs(currentColumn - startColumn);

		if (rowDiff > 0 || (rowDiff === 0 && colDiff <= 2)) {
			dragMode.value = 'column';
		} else {
			dragMode.value = 'free';
		}
	}

	dragEnd.value = date;
	dragEndColumn.value = currentColumn;
	dragEndRow.value = currentRow;
}

function handleDragEnd({ date }: { date: string }) {
	if (isDragging.value && dragStart.value) {
		let dates: string[] = [];

		const endColumn = dragEndColumn.value ?? getColumnForDate(date);
		const endRow = dragEndRow.value ?? getRowForDate(date);

		if (dragMode.value === 'column') {
			if (dragStartColumn.value === null || dragStartRow.value === null) return;
			const columns = getColumnRange(dragStartColumn.value, endColumn);
			dates = getDatesInColumnsAndRows(columns, dragStartRow.value, endRow);
		} else {
			const start = dayjs(dragStart.value);
			const end = dayjs(dragEnd.value || date);

			const rangeStart = start.isBefore(end) ? start : end;
			const rangeEnd = start.isBefore(end) ? end : start;

			let current = rangeStart;
			while (current.isBefore(rangeEnd) || current.isSame(rangeEnd, 'day')) {
				dates.push(current.format('YYYY-MM-DD'));
				current = current.add(1, 'day');
			}
		}

		if (dates.length > 0) {
			emit('dateRangeSelect', {
				dates,
				start: dates[0],
				end: dates[dates.length - 1],
				dragMode: dragMode.value
			});
		}
	}

	isDragging.value = false;
	dragStart.value = null;
	dragEnd.value = null;
	dragMode.value = null;
	dragStartColumn.value = null;
	dragEndColumn.value = null;
	dragStartRow.value = null;
	dragEndRow.value = null;
}

function handleDayClick({ date }: { date: string }) {
	if (!isDragging.value) {
		emit('dateClick', { date });
	}
}

function handleSetAvailabilityFromHover({ date }: { date: string }) {
	emit('setAvailabilityFromHover', { dates: [date], date });
}

function handleGlobalMouseUp() {
	if (isDragging.value && dragStart.value) {
		handleDragEnd({ date: dragEnd.value || dragStart.value });
	}
}

function findDateUnderPointer(clientX: number, clientY: number) {
	const offsets: Array<[number, number]> = [
		[0, 0],
		[6, 0],
		[-6, 0],
		[0, 6],
		[0, -6],
		[6, 6],
		[-6, 6],
		[6, -6],
		[-6, -6]
	];

	for (const [dx, dy] of offsets) {
		const el = document.elementFromPoint(clientX + dx, clientY + dy);
		if (!el) continue;
		const cell = el.closest?.('[id^=\"availability-day-\"]');
		const date = cell instanceof HTMLElement ? cell.id.replace(/^availability-day-/, '') : undefined;
		if (date) return date;
	}
	return null;
}

function handleGlobalMouseMove(e: MouseEvent) {
	if (!isDragging.value || !dragStart.value) return;
	if (typeof e.buttons === 'number' && (e.buttons & 1) !== 1) return;

	if (rafId.value) return;
	rafId.value = requestAnimationFrame(() => {
		rafId.value = null;
		const date = findDateUnderPointer(e.clientX, e.clientY);
		if (date && date !== dragEnd.value) {
			dragEnd.value = date;
		}
	});
}

// Get data for a specific day
function getAvailabilityForDate(dateStr: string) {
	return stores.availabilityStore.blocks.filter((b) => {
		if (!isAvailabilityBlockType(b.blockType)) return false;

		const start = dayjs(b.startDate);
		const end = dayjs(b.endDate);
		const date = dayjs(dateStr);
		return date.isSame(start, 'day') || date.isSame(end, 'day') || (date.isAfter(start) && date.isBefore(end));
	});
}

function getBlocksForDate(dateStr: string) {
	return stores.availabilityStore.blocks.filter((b) => {
		if (!isBlockingBlockType(b.blockType)) return false;

		const start = dayjs(b.startDate);
		const end = dayjs(b.endDate);
		const date = dayjs(dateStr);
		return date.isSame(start, 'day') || date.isSame(end, 'day') || (date.isAfter(start) && date.isBefore(end));
	});
}

function filterFutureMatches(matches: MatchRecord[], dateStr: string) {
	const now = dayjs();
	const day = dayjs(dateStr).startOf('day');
	if (day.isBefore(now.startOf('day'))) return [];

	return (matches || []).filter((m) => {
		const mDateStr = m?.matchDate ? dayjs(m.matchDate).format('YYYY-MM-DD') : dateStr;
		const mDay = dayjs(mDateStr).startOf('day');
		if (mDay.isAfter(now.startOf('day'))) return true;

		if (mDay.isSame(now.startOf('day'))) {
			if (!m?.matchTime) return true;
			const dt = dayjs(`${mDateStr}T${m.matchTime}`);
			return dt.isSame(now) || dt.isAfter(now);
		}
		return false;
	});
}

function getMatchesForDate(dateStr: string) {
	const suggestion = stores.availabilityStore.suggestions.find((s) => dayjs(s.date).format('YYYY-MM-DD') === dateStr);
	return filterFutureMatches((suggestion?.matches || []) as MatchRecord[], dateStr);
}

function getMatchCountForDate(dateStr: string) {
	const suggestion = stores.availabilityStore.suggestions.find((s) => dayjs(s.date).format('YYYY-MM-DD') === dateStr);
	if (!suggestion) return 0;

	const detailedMatches = Array.isArray(suggestion.matches) ? (suggestion.matches as MatchRecord[]) : [];
	if (detailedMatches.length > 0) {
		return filterFutureMatches(detailedMatches, dateStr).length;
	}

	const summaryCount = Number(suggestion.matchCount);
	return Number.isFinite(summaryCount) && summaryCount > 0 ? summaryCount : 0;
}

// Lifecycle
onMounted(() => {
	document.addEventListener('mouseup', handleGlobalMouseUp);
	document.addEventListener('mousemove', handleGlobalMouseMove, { passive: true });
});

onBeforeUnmount(() => {
	document.removeEventListener('mouseup', handleGlobalMouseUp);
	document.removeEventListener('mousemove', handleGlobalMouseMove);
	if (rafId.value) cancelAnimationFrame(rafId.value);
});

const hasInitialized = ref(false);
watch(
	() => props.initialDate,
	(newDate, oldDate) => {
		if (!hasInitialized.value) {
			currentDate.value = newDate ? dayjs(newDate) : dayjs();
			hasInitialized.value = true;
		} else if (newDate && oldDate) {
			const newDay = dayjs(newDate);
			const oldDay = dayjs(oldDate);
			if (!newDay.isSame(oldDay, 'day')) {
				currentDate.value = newDay;
			}
		}
	},
	{ immediate: true }
);

defineExpose({
	selectAllSaturdays,
	selectAllSundays,
	selectAllWeekends,
	previousMonthName,
	copyPreviousMonth
});
</script>

<template>
	<div class="bg-[var(--egs-content-background)] rounded-xl overflow-hidden flex flex-col flex-1 min-h-0" :class="{ 'mobile-calendar': mobileMode }">
		<!-- Header -->
		<div class="flex items-center justify-between py-3 border-b border-surface">
			<div class="flex items-center gap-3">
				<button
					class="flex items-center justify-center w-9 h-9 border-none rounded-lg bg-[var(--egs-content-hover)] text-color cursor-pointer transition-colors hover:bg-[var(--egs-content-hover)] active:bg-[var(--egs-content-border)]"
					@click="prev"
					:aria-label="view === 'week' ? 'Previous week' : 'Previous month'"
				>
					<font-awesome-icon :icon="['fas', 'chevron-left']" />
				</button>

				<div>
					<div class="flex flex-col items-center gap-0.5">
						<h2 class="text-xl font-semibold text-color cursor-pointer py-1 px-3 rounded-md transition-colors hover:bg-[var(--egs-content-hover)]" :class="{ '!text-base': mobileMode }" @click="goToToday">
							{{ headerTitle }}
						</h2>

						<div v-if="view === 'month'" id="month-match-summary" class="flex items-center justify-center gap-1 text-[0.7rem] leading-tight">
							<span class="text-color-secondary">{{ visibleMonthMatchSummary }}</span>
							<template v-if="visibleMonthAvailabilitySummary">
								<span class="text-color-secondary" aria-hidden="true">·</span>
								<span :class="visibleMonthAttentionCount > 0 ? 'font-medium text-orange-600 dark:text-orange-300' : 'text-green-600 dark:text-green-300'">
									{{ visibleMonthAvailabilitySummary }}
								</span>
							</template>
						</div>

						<button
							v-if="todayInDifferentMonth"
							class="inline-block p-0 border-none bg-transparent text-color-secondary text-[0.7rem] font-normal cursor-pointer opacity-70 transition-all hover:opacity-100 hover:text-primary hover:underline"
							title="Click to go to today"
							@click="goToToday"
						>
							Today is {{ todayFormatted }}
						</button>
					</div>
				</div>

				<button
					class="flex items-center justify-center w-9 h-9 border-none rounded-lg bg-[var(--egs-content-hover)] text-color cursor-pointer transition-colors hover:bg-[var(--egs-content-hover)] active:bg-[var(--egs-content-border)]"
					:class="{ '!w-8 !h-8': mobileMode }"
					@click="next"
					:aria-label="view === 'week' ? 'Next week' : 'Next month'"
				>
					<font-awesome-icon :icon="['fas', 'chevron-right']" />
				</button>
			</div>

			<div v-if="!mobileMode" class="flex items-center gap-3">
				<!-- Jump to month button -->
				<div class="relative">
					<button
						class="flex items-center gap-1.5 py-1.5 px-3 border border-surface rounded-lg bg-[var(--egs-content-background)] text-color-secondary text-[0.8125rem] font-medium cursor-pointer transition-all hover:border-primary hover:text-primary"
						title="Jump to a specific month"
						@click="openJumpPicker"
					>
						<font-awesome-icon :icon="['fas', 'calendar-alt']" />
						<span>Jump to…</span>
					</button>

					<!-- Jump picker popover -->
					<Transition name="picker-fade">
						<div v-if="showJumpPicker" class="absolute top-[calc(100%+8px)] right-0 z-50 w-[200px] bg-[var(--egs-content-background)] border border-surface rounded-xl shadow-dropdown" @click.stop>
							<div class="flex items-center justify-between py-3 px-3.5 border-b border-surface font-semibold text-sm text-color">
								<span>Jump to Month</span>
								<button class="flex items-center justify-center w-6 h-6 border-none rounded-md bg-transparent text-color-secondary cursor-pointer hover:bg-[var(--egs-content-hover)]" @click="closeJumpPicker">
									<font-awesome-icon :icon="['fas', 'xmark']" />
								</button>
							</div>
							<div class="p-3.5 flex flex-col gap-2.5">
								<div class="flex flex-col gap-1">
									<label class="text-xs font-medium text-color-secondary">Month</label>
									<select
										v-model="jumpMonth"
										class="w-full h-9 px-2.5 text-sm text-color bg-[var(--egs-content-background)] border border-surface rounded-md cursor-pointer outline-none focus:border-primary focus:ring-2 focus:ring-primary/20"
									>
										<option v-for="(name, idx) in monthNames" :key="idx" :value="idx">{{ name }}</option>
									</select>
								</div>
								<div class="flex flex-col gap-1">
									<label class="text-xs font-medium text-color-secondary">Year</label>
									<select
										v-model="jumpYear"
										class="w-full h-9 px-2.5 text-sm text-color bg-[var(--egs-content-background)] border border-surface rounded-md cursor-pointer outline-none focus:border-primary focus:ring-2 focus:ring-primary/20"
									>
										<option v-for="year in yearOptions" :key="year" :value="year">{{ year }}</option>
									</select>
								</div>
							</div>
							<div class="flex gap-2 p-3.5 border-t border-surface">
								<button
									class="flex-1 h-[34px] rounded-md text-[0.8125rem] font-medium cursor-pointer transition-all border border-surface bg-[var(--egs-content-background)] text-color-secondary hover:bg-[var(--egs-content-hover)]"
									@click="closeJumpPicker"
								>
									Cancel
								</button>
								<button class="flex-1 h-[34px] rounded-md text-[0.8125rem] font-medium cursor-pointer transition-all border-none bg-primary text-white hover:bg-primary-600" @click="confirmJump">Go</button>
							</div>
						</div>
					</Transition>
				</div>

				<div class="flex bg-[var(--egs-content-hover)] rounded-lg p-[3px]">
					<button
						class="py-1.5 px-3 border-none rounded-md bg-transparent text-color-secondary text-[0.8125rem] font-medium cursor-pointer transition-all hover:text-color"
						:class="{ '!bg-[var(--egs-content-background)] !text-color shadow-sm': view === 'month' }"
						@click="setView('month')"
					>
						Month
					</button>
					<button
						class="py-1.5 px-3 border-none rounded-md bg-transparent text-color-secondary text-[0.8125rem] font-medium cursor-pointer transition-all hover:text-color"
						:class="{ '!bg-[var(--egs-content-background)] !text-color shadow-sm': view === 'week' }"
						@click="setView('week')"
					>
						Week
					</button>
				</div>
			</div>
		</div>

		<!-- Month View Grid -->
		<div v-if="view === 'month'" class="py-2 flex-1 flex flex-col min-h-0 overflow-y-auto">
			<!-- Week day headers -->
			<div class="grid grid-cols-7 gap-[3px] mb-1" :class="{ '!gap-0.5': mobileMode }">
				<div
					v-for="day in weekDayHeaders"
					:key="day.key"
					class="text-center text-[0.7rem] font-semibold text-color-secondary uppercase tracking-wide py-1.5"
					:class="{ '!text-primary !font-bold': day.isWeekend, '!text-[0.65rem] !py-1': mobileMode }"
				>
					{{ day.label }}
				</div>
			</div>

			<!-- Weeks -->
			<div class="flex flex-col gap-[3px] flex-1 min-h-0 calendar-weeks-animate" :class="{ '!gap-0.5': mobileMode }">
				<div v-for="(week, weekIndex) in weeks" :key="weekIndex" class="grid grid-cols-7 gap-[3px] flex-1 min-h-[50px]" :class="{ '!gap-0.5': mobileMode }">
					<DayCell
						v-for="day in week"
						:key="day.date"
						:date="day.date"
						:isToday="day.isToday"
						:isCurrentMonth="day.isCurrentMonth"
						:isSelected="selectedDateRange.has(day.date)"
						:isDragOver="isDragging && dragEnd === day.date"
						:isWeekend="day.isWeekend"
						:availabilityEntries="getAvailabilityForDate(day.date)"
						:blockEntries="getBlocksForDate(day.date)"
						:matches="getMatchesForDate(day.date)"
						:matchCount="getMatchCountForDate(day.date)"
						:mobileMode="mobileMode"
						@click="handleDayClick"
						@dragStart="handleDragStart"
						@dragEnter="handleDragEnter"
						@dragEnd="handleDragEnd"
						@setAvailability="handleSetAvailabilityFromHover"
					/>
				</div>
			</div>
		</div>

		<!-- Week View Grid -->
		<div v-if="view === 'week'" class="py-2 flex-1 flex flex-col min-h-0 overflow-hidden">
			<!-- Week day headers -->
			<div class="grid grid-cols-7 gap-[3px] mb-1">
				<div v-for="day in weekDayHeaders" :key="day.key" class="text-center text-[0.7rem] font-semibold text-color-secondary uppercase tracking-wide py-1.5" :class="{ '!text-primary !font-bold': day.isWeekend }">
					{{ day.label }}
				</div>
			</div>

			<!-- Single week row with larger cells -->
			<div class="flex flex-col flex-1 min-h-[200px]">
				<div class="grid grid-cols-7 gap-[3px] h-full min-h-[200px]">
					<DayCell
						v-for="day in weekDays"
						:key="day.date"
						:date="day.date"
						:isToday="day.isToday"
						:isCurrentMonth="true"
						:isSelected="selectedDateRange.has(day.date)"
						:isDragOver="isDragging && dragEnd === day.date"
						:isWeekend="day.isWeekend"
						:availabilityEntries="getAvailabilityForDate(day.date)"
						:blockEntries="getBlocksForDate(day.date)"
						:matches="getMatchesForDate(day.date)"
						:matchCount="getMatchCountForDate(day.date)"
						:mobileMode="mobileMode"
						@click="handleDayClick"
						@dragStart="handleDragStart"
						@dragEnter="handleDragEnter"
						@dragEnd="handleDragEnd"
						@setAvailability="handleSetAvailabilityFromHover"
					/>
				</div>
			</div>
		</div>

		<!-- Compact Legend -->
		<div class="shrink-0 space-y-2 px-2 py-2 border-t border-surface bg-[var(--egs-content-hover)] text-xs text-color-secondary" aria-label="Availability legend">
			<div class="flex flex-wrap justify-center gap-x-4 gap-y-1.5">
				<div class="flex items-center gap-1.5">
					<span class="w-6 h-3 rounded border-l-[3px] border-green-500 bg-green-500/10 dark:bg-green-500/20" aria-hidden="true"></span>
					<span>Fully available</span>
				</div>
				<div class="flex items-center gap-1.5">
					<span class="availability-partial-green w-6 h-3 rounded border-l-[3px] border-green-500 bg-green-500/10 dark:bg-green-500/20" aria-hidden="true"></span>
					<span>Partially available</span>
				</div>
				<div class="flex items-center gap-1.5">
					<span class="w-6 h-3 rounded border-l-[3px] border-red-500 bg-red-500/10 dark:bg-red-500/20" aria-hidden="true"></span>
					<span>Fully blocked</span>
				</div>
				<div class="flex items-center gap-1.5">
					<span class="availability-partial-red w-6 h-3 rounded border-l-[3px] border-red-500 bg-red-500/10 dark:bg-red-500/20" aria-hidden="true"></span>
					<span>Partially blocked</span>
				</div>
				<div class="flex items-center gap-1.5">
					<span class="w-6 h-3 rounded border border-surface bg-[var(--egs-content-background)]" aria-hidden="true"></span>
					<span>Not set</span>
				</div>
				<div class="flex items-center gap-1.5">
					<span class="w-6 h-3 rounded border-l-[3px] border-blue-500 bg-blue-500/10 dark:bg-blue-500/20" aria-hidden="true"></span>
					<span>Assigned match</span>
				</div>
			</div>
			<div class="flex flex-wrap justify-center gap-x-4 gap-y-1.5">
				<div class="flex items-center gap-1.5"><font-awesome-icon :icon="['fas', 'clock']" aria-hidden="true" /><span>Specific times</span></div>
				<div class="flex items-center gap-1.5"><font-awesome-icon :icon="['fas', 'users']" aria-hidden="true" /><span>Specific groups / clients</span></div>
				<div class="flex items-center gap-1.5"><span class="w-2.5 h-2.5 rounded-full bg-orange-500" aria-hidden="true"></span><span>Matches scheduled</span></div>
			</div>
		</div>

		<!-- Mobile hint -->
		<div v-if="mobileMode" class="text-center py-2.5 text-xs text-color-secondary border-t border-surface">
			<span>Tap a date for details • drag to select range</span>
		</div>
		<div v-else class="text-center py-2.5 text-xs text-color-secondary border-t border-surface">
			<span>click a date for details • click and drag to select range</span>
		</div>
	</div>
</template>
